import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import TextField from '@Components/Shared/Controls/TextField';
import Toggle from '@Components/Shared/Controls/Toggle';
import SortDropdown from '@Components/Shared/SortDropdown';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useState, useEffect } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { sortDistanceWithEnteredLocation, runSortWithCurrentLocation } from '@Components/Utilities/DistanceSortHelper';
import { unsetScrollPosition } from '@Components/Utilities/SandhillsUtility';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { hasCurrentLongitudeAndLatitudeCookies } from '@Components/Utilities/CookieHelper';

const ShowClosestFirstToggle = (props) => {
    const [showDistanceError, setShowDistanceError] = useState(props.ShowDistanceSortError);
    const [userEnteredLocation, setUserEnteredLocation] = useState(props.CurrentUserFormattedAddress);
    const routes = useRecoilValue(atoms.routes);
    const windowSize = useWindowSize();
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);

    const toggleChange = (isChecked) => {
        if (props.setDisplaySCFInput) {
            props.setDisplaySCFInput(isChecked);
        }
        if (showDistanceError) {
            setUserEnteredLocation((props.CurrentUserFormattedAddress && isChecked) ? props.CurrentUserFormattedAddress : "");
        }
        if (isChecked) {
            props.onSortChange(props.distanceSortID);
        }
        else {
            props.onSortChange(props.defaultSortID);
        }
    };

    useEffect(function updateCurrentLocation() {
        if (typeof props.CurrentUserFormattedAddress !== 'undefined' && props.CurrentUserFormattedAddress != userEnteredLocation) {
            setUserEnteredLocation(props.CurrentUserFormattedAddress);
        }
    }, [props.CurrentUserFormattedAddress]);

    useEffect(function updateSortError() {
        setShowDistanceError(false);
    }, [props.DistanceSortIsSelected]);

    const sortWithCurrentLocation = () => {
        if (props.setShowDistanceModal && !(hasCurrentLongitudeAndLatitudeCookies())) {
            props.setShowDistanceModal(true)
        } else {
            runSortWithCurrentLocation(
                routes.FormattedLocationStringFromCurrentCoordinates,
                routes.AjaxGetCoordinatesFromLocationStringUrl,
                props.CurrentAuthenticatedUserLocationForCoordinates
            ).then(location => {
                unsetScrollPosition();
                setShowDistanceError(false);
                props.OnUpdate({
                    Latitude: location.latitude,
                    Longitude: location.longitude,
                    UseCurrentLocation: true
                });
                setCurrentUser(user => {
                    return {
                        ...user,
                        CurrentLocation: {
                            ...user.CurrentLocation,
                            FormattedAddress: location.formattedAddress
                        }
                    };
                });
            }).catch(() => {
                setShowDistanceError(true);
            });
        }
    };

    const sortWithEnteredLocation = (e, enteredLocation) => {
        sortDistanceWithEnteredLocation(routes.AjaxGetCoordinatesFromLocationStringUrl, (enteredLocation || userEnteredLocation))
            .then(location => {
                unsetScrollPosition();
                setShowDistanceError(false);
                props.OnUpdate({
                    Latitude: location.latitude,
                    Longitude: location.longitude
                });
                setCurrentUser(user => {
                    return {
                        ...user,
                        CurrentLocation: {
                            ...user.CurrentLocation,
                            FormattedAddress: location.formattedAddress
                        }
                    };
                });
            })
            .catch(() => {
                setShowDistanceError(true);
            });
    };

    const getAutoCompleteCallback = () => {
        return (e, ui) => {
            setUserEnteredLocation(ui.item.value);
            sortWithEnteredLocation(e, ui.item.value);
        }
    };

    const getAutoCompleteUrl = (e) => {
        return routes.LocationAutosuggestUrl + encodeURI(e.target.value.trim());
    };

    const onKeyPress = (e) => {
        if (e.code === 'Enter') {
            sortWithEnteredLocation(e);
        }
    };

    const onFieldBlur = (e) => {
        if (props.CurrentUserFormattedAddress != userEnteredLocation) {
            if (!userEnteredLocation) {
                setUserEnteredLocation(props.CurrentUserFormattedAddress);
            }
            else {
                sortWithEnteredLocation(e);
            }
        }
    };

    const handleFieldChange = (e) => {
        setUserEnteredLocation(e.target.value);
    };

    return (
        <div className='show-closest-first-container'>
            <div className='show-closest-first'>
                <Toggle
                    classNames='closest-toggle'
                    id={'show-closest-first-toggle'}
                    IsChecked={props.DistanceSortIsSelected}
                    onChange={toggleChange}
                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.showclosestfirst' />}
                />
                {props.displaySCFInput &&
                    <div className='text-box-with-built-in-button'>
                        <label htmlFor='sort-location' className='visually-hidden'><TranslationText tKey='sandhills.citystatezip' /></label>
                        <TextField
                            name='sort-location-autosuggest'
                            value={userEnteredLocation}
                            type='text'
                            required={false}
                            id='sort-location'
                            onFieldChanged={handleFieldChange}
                            updateField={setUserEnteredLocation}
                            focus={false}
                            placeholder={translator.translate('sandhills.citystatezip')}
                            autoComplete={{
                                ajaxUrl: (e) => getAutoCompleteUrl(e),
                                callback: getAutoCompleteCallback()
                            }}
                            autoCompleteDelay='1'
                            autoSuggestWidth={330}
                            onKeyPress={onKeyPress}
                            onFieldBlur={onFieldBlur}
                        />
                        <button className='current-loc-btn ts-button' aria-label='Use My Current Location' onClick={sortWithCurrentLocation}>
                            <i className='fas fa-crosshairs' aria-hidden='true'></i>
                        </button>
                    </div>
                }
                {currentUser.DisplayFTIToggle && !props.AuctionResultsPage && props.IsListingsPage &&
                    <div className={props.IsFTISearch ? 'fti-toggle pad-it' : 'fti-toggle'}>
                        <div id='wholesale-toggle' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.toggleFTISearch(); } }} aria-label={translator.translate('sandhills.wholesaleview')} title={ translator.translate('sandhills.wholesaleview') } className={props.IsFTISearch ? 'sort active' : 'sort'} onClick={() => { props.toggleFTISearch(); }}>
                            {!currentSite?.Site?.IsRVUniverse &&
                                <img src={routes.CDNBaseUrl + '/Images/Logos/FastTrack/FastTrackIronIcon.png'} alt='FTI Logo' />
                            }
                            {translator.translate('sandhills.crm.dscrm.wholesale')}
                        </div>
                        <div id='sale-toggle' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.toggleFTISearch(); } }} aria-label={translator.translate('sandhills.forsaleview')} title={translator.translate('sandhills.forsaleview')} className={props.IsFTISearch ? 'sort' : 'sort active'} onClick={() => { props.toggleFTISearch(); }}>
                            {translator.translate('sandhills.all')}
                        </div>
                    </div>
                }
                {!windowSize.isMobile && !props.IsFTISearch && props.IsListingsPage &&
                    <div className='page-type-toggle' style={{ 'marginLeft': props.CanViewFTI && !props.AuctionResultsPage ? 'unset' : 'auto' }}>
                        <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.setUseGridView(); } }} id='list-toggle' aria-label={translator.translate('sandhills.listview')} title={translator.translate('sandhills.listview')} className={props.UseNewListPageDesign ? 'sort' : 'sort active'} onClick={() => { props.setUseGridView(); }}>
                            <i className='fas fa-list' />
                        </div>
                        <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.setUseGridView(); } }} id='grid-toggle' aria-label={translator.translate('sandhills.thumbnailview')} title={translator.translate('sandhills.thumbnailview')} className={props.UseNewListPageDesign ? 'sort active' : 'sort'} onClick={() => { props.setUseGridView(); }}>
                            <i className='fas fa-table-cells-large' />
                        </div>
                    </div>
                }
                {(props.SortOrderItems && props.SortOrderItems.length > 0) && !windowSize.isMobile &&
                    <SortDropdown
                        IsDesktop={props.IsDesktop}
                        SortOptions={props.SortOrderItems}
                        OnChange={props.OnChange}
                        SelectedValue={props.SelectedValue}
                        SortButton={props.SortButton}
                        ShowMobileSort={props.ShowMobileSort}
                        toggleMobileSort={props.toggleMobileSort}
                        {...(props.setShowDistanceModal && { setShowDistanceModal: props.setShowDistanceModal })}
                    />}
            </div>
            {showDistanceError &&
                <div className='error'>
                    <p><TranslationText tKey='sandhills.wecouldnotretrieveyourlocation' /></p>
                </div>}
        </div>
    );
};

export default ShowClosestFirstToggle;